import "./App.css";
import { Routes, Route, useLocation } from "react-router-dom";

/* Importing pages for the dealership product*/
import NotFound from "./pages/NotFound/NotFound";
import Quiz from "./pages/Quiz/Quiz";
import NoMatches from "./components/NoMatches/NoMatches";
import Gallery from "./pages/Gallery/Gallery";
import DealershipHome from "./pages/DealershipHome/DealershipHome";
import { AIChatInterface } from "./components/AIChatInterface/AIChatInterface";
import { useDealershipContext } from "./contexts/dealershipContext";
import { useEffect, useRef } from "react";
import { useVersionCheck } from "./hooks/useVersionCheck";
import UpdateBanner from "./components/UpdateBanner/UpdateBanner";

function App() {
  const { updateAvailable, refreshApp } = useVersionCheck();

  const channelRef = useRef(null);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const audioParam = queryParams.get("audio");

  const isSingleTabEnforced = audioParam === "2";

  useEffect(() => {
    if (!isSingleTabEnforced) return;
    // if(channelRef?.current) return;

    channelRef.current = new BroadcastChannel("dealership-app-tab");

    const tabId = Date.now() + Math.random();

    channelRef.current.postMessage({
      type: "TAB_OPENED",
      tabId: tabId,
    });

    const handleMessage = (event) => {
      if (event.data.type === "TAB_OPENED" && event.data.tabId !== tabId) {
        window.focus();
        channelRef.current.postMessage({
          type: "TAB_EXISTS",
          existingTabId: tabId,
        });
      } else if (event.data.type === "TAB_EXISTS") {
        alert(
          "Application is already open in another tab. This tab will be closed."
        );
        window.close();
        setTimeout(() => {
          window.location.href = "about:blank";
        }, 100);
      } else if (event.data.type === "TAB_FOCUSED") {
        window.focus();
      }
    };

    channelRef.current.addEventListener("message", handleMessage);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        channelRef.current.postMessage({
          type: "TAB_FOCUSED",
          tabId: tabId,
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (channelRef.current) {
        channelRef.current.removeEventListener("message", handleMessage);
        channelRef.current.close();
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (channelRef.current) {
        channelRef.current.postMessage({
          type: "TAB_CLOSING",
        });
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const { baseURL } = useDealershipContext();
  const wakeLockRef = useRef(null);

  useEffect(() => {
    async function requestWakeLock() {
      if (!("wakeLock" in navigator)) {
        console.warn("Wake Lock API not supported in this browser.");
        return;
      }

      try {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
        console.log("Wake Lock enabled");

        wakeLockRef.current.onrelease = () => {
          console.log("Wake Lock was released");
          if (document.visibilityState === "visible") {
            requestWakeLock();
          }
        };
      } catch (err) {
        console.error("WakeLock error:", err);
        if (document.visibilityState === "visible") {
          console.log("Retrying wake lock request in 1s...");
          setTimeout(requestWakeLock, 1000);
        }
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        requestWakeLock();
      } else {
        console.log("Wake Lock paused (tab hidden).");
      }
    };

    if (document.visibilityState === "visible") {
      requestWakeLock();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    };
  }, []);

  return (
    <div className="App">
      {updateAvailable && <UpdateBanner onRefresh={refreshApp} />}

      <Routes>
        <Route path="/" element={<DealershipHome />} />
        <Route path={`${baseURL}`} element={<DealershipHome />} />
        <Route path={`${baseURL}matchmaker-quiz/`} element={<Quiz />} />
        <Route path={`${baseURL}matches`} element={<Gallery />} />
        <Route path={`${baseURL}matches/zero`} element={<NoMatches />} />
        <Route
          path={`${baseURL}testdrive`}
          element={<Quiz key={location.search} />}
        />
        <Route path={`${baseURL}update-profile`} element={<Quiz />} />
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<NotFound />} />
        <Route path="/chat" element={<AIChatInterface />} />
      </Routes>
    </div>
  );
}

export default App;
